import { OwnerId, useGetWorkspacesQuery, type SinglePlan } from 'prove-common'
import { resolvePlanWorkspaceId } from '../../workspaces/workspaces'

export type DelegationRoot = {
  /** Nivået fordelingen starter på. Tom når divisjonen ikke kan bestemmes. */
  path: OwnerId[]
  /** Navnet på tilsynsdivisjonen, brukt som tittel og første brødsmule. */
  label?: string
  isPending: boolean
  isError: boolean
}

/**
 * Fordelingen starter i tilsynsdivisjonen planen hører til, slik at brukeren
 * slipper å velge divisjon på nytt inne i en divisjon hen allerede jobber i.
 *
 * Dette henger på planen, ikke på om den nye arbeidsflaten for tilsynsdivisjoner
 * er slått på – en plan hører til den samme divisjonen uansett hvordan brukeren
 * navigerte seg fram til den.
 */
export const useDelegationRootPath = (plan?: SinglePlan): DelegationRoot => {
  const { data: workspaces, isPending, isError } = useGetWorkspacesQuery()

  const workspaceId = resolvePlanWorkspaceId(plan)
  if (workspaceId == null) {
    return { path: [], isPending: false, isError: false }
  }
  if (isPending) return { path: [], isPending, isError }
  if (isError) return { path: [], isPending, isError }

  const workspace = (workspaces ?? []).find(
    (candidate) => candidate.id === workspaceId
  )
  if (workspace == null) {
    return { path: [], isPending: false, isError: true }
  }

  const ownerId =
    workspace.ownerId == null
      ? undefined
      : OwnerId.fromString(workspace.ownerId)

  if (ownerId?.type !== 'region') {
    // Divisjonen finnes ennå ikke i organisasjonsstrukturen, så vi vet ikke
    // hvilke avdelinger som hører til den.
    return {
      path: [],
      label: workspace.name,
      isPending: false,
      isError: false
    }
  }

  return {
    path: [ownerId],
    label: workspace.name,
    isPending: false,
    isError: false
  }
}
